import { Bike, CarFront, ChevronRight, Clock3, Footprints, GripVertical, Hotel, LoaderCircle, MapPin, Mountain, Plus, Route as RouteIcon, ShieldAlert, Trash2, TrendingDown, TrendingUp, X } from "lucide-react";
import { useState } from "react";
import { buildDaySchedule, daySegmentMetrics, formatClock, formatTravelDuration } from "../lib/schedule";
import type { Day, ElevationFocus, Navigation, Place, TravelMode } from "../types";

interface Props {
  routeId: string;
  routeColor?: string;
  nodes: Record<string, Place>;
  days: Day[];
  navigation: Record<string, Navigation>;
  selectedDayId: string | null;
  selectedPlaceId: string | null;
  viewScope: "day" | "all";
  elevationFocus: ElevationFocus | null;
  onElevationFocus: (focus: ElevationFocus | null) => void;
  onSelectDay: (id: string) => void;
  onSelectPlace: (id: string | null) => void;
  onAddDay: () => void;
  onReorder: (dayId: string, from: number, to: number) => void;
  onRemove: (dayId: string, index: number) => void;
  canEdit: boolean;
  showPlaceDetails?: boolean;
}

export function ItineraryPanel({ routeId, routeColor, nodes, days, navigation, selectedDayId, selectedPlaceId, viewScope, elevationFocus, onElevationFocus, onSelectDay, onSelectPlace, onAddDay, onReorder, onRemove, canEdit, showPlaceDetails = false }: Props) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const day = days.find((item) => item.id === selectedDayId) ?? days[0];
  const nav = day ? navigation[`${routeId}::${day.id}`] : undefined;
  const checks = day ? dayChecks(day, nodes, nav, canEdit) : [];
  const timings = day ? buildDaySchedule(day, nav, nodes) : [];
  const segments = day ? daySegmentMetrics(day, nav, nodes) : [];
  const finish = timings.at(-1)?.departureMinutes;

  return (
    <aside className={`itinerary-panel${canEdit ? "" : " is-readonly"}`}>
      <div className="panel-heading">
        <div><span className="panel-kicker">逐日路书</span><h1>{day?.name ?? "还没有行程日"}</h1></div>
        {canEdit && <button className="icon-button" onClick={onAddDay} title="新增一天"><Plus size={19} /></button>}
      </div>
      <div className="day-rail" role="tablist" aria-label="选择行程日">
        {days.map((item, index) => <button key={item.id} role="tab" aria-selected={item.id === day?.id} className={item.id === day?.id ? "is-active" : ""} onClick={() => onSelectDay(item.id)}><i className="day-color-dot" style={{ backgroundColor: item.color || routeColor || "#3977A8" }} aria-hidden="true" /><span>{index + 1}</span><small>{item.date?.slice(5) || item.id}</small></button>)}
        {canEdit && <button className="day-add" onClick={onAddDay} aria-label="新增一天"><Plus size={16} /></button>}
      </div>
      {day && <>
        <div className="day-summary">
          <span>{modeIcon(day.mode)}{modeCopy(day.mode)}</span>
          <span><RouteIcon size={15} />{nav?.distanceMeters ? `${(nav.distanceMeters / 1000).toFixed(0)} km` : "待规划"}</span>
          <span><Clock3 size={15} />{nav?.durationSeconds ? formatTravelDuration(nav.durationSeconds) : "—"}</span>
        </div>
        <div className="day-clock-band">
          <span><small>出发</small><strong>{day.departureTime || "09:00"}</strong></span>
          <i />
          <span><small>预计结束</small><strong>{finish === undefined ? "—" : formatClock(finish)}</strong></span>
        </div>
        <ElevationProfile routeId={routeId} days={viewScope === "all" ? days : [day]} nodes={nodes} navigation={navigation} fullTrip={viewScope === "all"} focus={elevationFocus} onFocus={onElevationFocus} fallbackColor={routeColor} />
        {day.detail && <p className="day-note">{day.detail}</p>}
        {!!checks.length && <div className="day-checks"><span><ShieldAlert size={15} />行程检查</span>{checks.map((check) => <p key={check}>{check}</p>)}</div>}
        <div className="timeline" aria-label={`${day.name}地点顺序`}>
          {day.stops.map((stop, index) => {
            const place = nodes[stop.node];
            const timing = timings[index];
            const segment = segments[index];
            if (!place) return null;
            return <div key={`${stop.node}-${index}`} className="timeline-unit">
              <div className="timeline-row">
                <span className="stop-time"><strong>{timing ? formatClock(timing.arrivalMinutes) : "—"}</strong><small>{index ? "预计到达" : "集合出发"}</small></span>
                {canEdit && <button className="drag-handle" draggable onDragStart={() => setDragIndex(index)} onDragOver={(event) => event.preventDefault()} onDrop={() => { if (dragIndex !== null) onReorder(day.id, dragIndex, index); setDragIndex(null); }} aria-label={`拖动调整 ${place.name} 顺序`}><GripVertical size={16} /></button>}
                <button className={`stop-card${selectedPlaceId === stop.node ? " is-selected" : ""}`} onClick={() => onSelectPlace(stop.node)}>
                  <span className="stop-index">{index + 1}</span>
                  <span className="stop-copy"><span className="stop-title"><strong>{place.name}</strong>{place.elevation !== undefined && <em className="elevation-chip"><Mountain size={11} />{Math.round(place.elevation)} m</em>}</span><small>{timing?.dwellMinutes ? `停留 ${formatMinutes(timing.dwellMinutes)}` : index === day.stops.length - 1 ? "当天终点" : "短暂停靠"}</small><span className="stop-tags">{stop.tags?.slice(0, 2).map((tag) => <em key={tag}>{tag === "住宿点" && <Hotel size={12} />}{tag}</em>)}</span></span>
                  <ChevronRight size={17} className="stop-arrow" />
                </button>
                {canEdit && <button className="stop-delete" onClick={() => {
                  const references = days.reduce((count, candidate) => count + candidate.stops.filter((item) => item.node === stop.node).length, 0);
                  if (references > 1 && !window.confirm(`“${place.name}”还被其他行程日引用。确定只从${day.name}移除吗？`)) return;
                  onRemove(day.id, index);
                }} aria-label={`从当天移除 ${place.name}`}><Trash2 size={15} /></button>}
              </div>
              {showPlaceDetails && selectedPlaceId === stop.node && <section className="itinerary-place-detail">
                <button onClick={() => onSelectPlace(null)} aria-label="关闭地点资料"><X size={15} /></button>
                {place.amapAddress && <p>{place.amapAddress}</p>}
                <div><small>{place.elevation == null ? "海拔暂无数据" : `海拔 ${Math.round(place.elevation)} m`}</small><small>{place.coord[0].toFixed(5)}, {place.coord[1].toFixed(5)}</small></div>
                {place.note && <p>{place.note}</p>}
              </section>}
              {index < day.stops.length - 1 && <div className="segment-row">
                <span className="segment-depart">{timing ? formatClock(timing.departureMinutes) : "—"}<small>离开</small></span>
                <span className="segment-line">{modeIcon(day.mode)}</span>
                <div className={`segment-card${segment.estimated ? " is-estimated" : ""}`}>
                  <span>{place.name} → {nodes[day.stops[index + 1].node]?.name}</span>
                  <strong>{segment.distanceMeters ? formatDistance(segment.distanceMeters) : "距离待重算"}<i />{segment.durationSeconds ? formatTravelDuration(segment.durationSeconds) : "时长待重算"}</strong>
                  {segment.estimated && canEdit && <small>按全日路线比例估算，重算道路后将更新</small>}
                </div>
              </div>}
            </div>;
          })}
          {!day.stops.length && <div className="empty-timeline"><MapPin size={28} /><strong>这一天还没有地点</strong>{canEdit && <span>从地图搜索地点，或使用“地图落点”添加。</span>}</div>}
        </div>
      </>}
    </aside>
  );
}

interface ProfilePoint {
  dayId: string;
  localDistanceKm: number;
  distanceKm: number;
  elevation: number;
  coord?: [number, number];
  placeId?: string;
  name?: string;
}

interface ProfileLandmark extends ProfilePoint {
  placeId: string;
  name: string;
}

interface ProfileSeries {
  day: Day;
  offsetKm: number;
  distanceKm: number;
  points: ProfilePoint[];
  landmarks: ProfileLandmark[];
  routed: boolean;
}

function ElevationProfile({ routeId, days, nodes, navigation, fullTrip, focus, onFocus, fallbackColor }: { routeId: string; days: Day[]; nodes: Record<string, Place>; navigation: Record<string, Navigation>; fullTrip: boolean; focus: ElevationFocus | null; onFocus: (focus: ElevationFocus | null) => void; fallbackColor?: string }) {
  let offsetKm = 0;
  const series: ProfileSeries[] = days.map((day) => {
    const route = navigation[`${routeId}::${day.id}`];
    const segments = daySegmentMetrics(day, route, nodes);
    let landmarkDistanceKm = 0;
    const landmarks = day.stops.flatMap<ProfileLandmark>((stop, index) => {
      if (index) landmarkDistanceKm += (segments[index - 1]?.distanceMeters ?? 0) / 1000;
      const place = nodes[stop.node];
      return place?.elevation === undefined ? [] : [{ dayId: day.id, placeId: stop.node, name: place.name, localDistanceKm: landmarkDistanceKm, distanceKm: offsetKm + landmarkDistanceKm, elevation: place.elevation, coord: place.coord }];
    });
    const routed = route?.profile?.filter((point) => Number.isFinite(point.distanceKm) && Number.isFinite(point.elevation)) ?? [];
    const rawPoints = routed.length >= 2 ? routed : landmarks;
    const points = rawPoints.map((point) => ({ dayId: day.id, localDistanceKm: point.distanceKm, distanceKm: offsetKm + point.distanceKm, elevation: point.elevation, coord: point.coord }));
    const routeDistanceKm = Math.max(route?.distanceMeters ? route.distanceMeters / 1000 : 0, rawPoints.at(-1)?.distanceKm ?? 0, landmarkDistanceKm);
    const result = { day, offsetKm, distanceKm: routeDistanceKm, points, landmarks, routed: routed.length >= 2 };
    offsetKm += routeDistanceKm;
    return result;
  });
  const points = series.flatMap((item) => item.points);
  const landmarks = series.flatMap((item) => item.landmarks);
  const loading = series.some((item) => navigation[`${routeId}::${item.day.id}`]?.elevationStatus === "loading");
  const failed = series.every((item) => navigation[`${routeId}::${item.day.id}`]?.elevationStatus === "failed");
  if (points.length < 2 && loading) return <div className="elevation-empty is-loading"><LoaderCircle size={18} className="spin" /><span><strong>正在生成路线海拔剖面</strong><small>沿道路轨迹自动采样，完成后同步更新坡面。</small></span></div>;
  if (points.length < 2 && failed) return <div className="elevation-empty is-failed"><Mountain size={18} /><span><strong>路线海拔剖面暂不可用</strong><small>道路轨迹已保留；点击“重算道路”可重新生成坡面。</small></span></div>;
  if (points.length < 2) return <div className="elevation-empty"><Mountain size={18} /><span><strong>路线海拔资料待生成</strong><small>完成道路规划后，将自动沿轨迹生成海拔剖面。</small></span></div>;
  const min = Math.min(...points.map((point) => point.elevation));
  const max = Math.max(...points.map((point) => point.elevation));
  const distance = Math.max(offsetKm, ...points.map((point) => point.distanceKm), 1);
  const range = Math.max(max - min, 1);
  const x = (distanceKm: number) => 10 + (distanceKm / distance) * 320;
  const y = (elevation: number) => 91 - ((elevation - min) / range) * 62;
  const activePoint = focus ? landmarks.find((point) => point.placeId === focus.placeId) ?? points.reduce<ProfilePoint | undefined>((nearest, point) => {
    if (point.dayId !== focus.dayId) return nearest;
    return !nearest || Math.abs(point.localDistanceKm - focus.distanceKm) < Math.abs(nearest.localDistanceKm - focus.distanceKm) ? point : nearest;
  }, undefined) : undefined;
  let ascent = 0;
  let descent = 0;
  for (const item of series) item.points.forEach((point, index) => { if (!index) return; const delta = point.elevation - item.points[index - 1].elevation; if (delta > 0) ascent += delta; else descent += Math.abs(delta); });
  const updateFocus = (event: React.PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const distanceAtPointer = Math.max(0, Math.min(distance, (((event.clientX - rect.left) / rect.width) * 340 - 10) / 320 * distance));
    const nearest = points.reduce((current, point) => Math.abs(point.distanceKm - distanceAtPointer) < Math.abs(current.distanceKm - distanceAtPointer) ? point : current);
    const landmark = landmarks.find((point) => point.dayId === nearest.dayId && Math.abs(point.localDistanceKm - nearest.localDistanceKm) < 0.05);
    onFocus({ dayId: nearest.dayId, distanceKm: nearest.localDistanceKm, elevation: nearest.elevation, coord: nearest.coord, placeId: landmark?.placeId });
  };
  const tooltipX = activePoint ? Math.max(13, Math.min(267, x(activePoint.distanceKm) - 31)) : 0;
  return <section className={`elevation-profile${fullTrip ? " is-full-trip" : ""}`} aria-label={fullTrip ? "全程海拔剖面" : "当天海拔剖面"}>
    <header><span><Mountain size={14} />{fullTrip ? `全程海拔剖面 · ${series.length} 天` : series[0].routed ? `路线海拔剖面 · ${points.length} 点` : `地点海拔预览 · ${landmarks.length}/${days[0].stops.length}`}</span><small className={activePoint ? "is-active" : ""}>{activePoint ? `${activePoint.distanceKm.toFixed(1)} km · ${Math.round(activePoint.elevation)} m` : `${Math.round(min)}–${Math.round(max)} m`}</small></header>
    <svg viewBox="0 0 340 108" role="img" aria-label={`海拔从 ${Math.round(min)} 米到 ${Math.round(max)} 米`} onPointerMove={updateFocus} onPointerLeave={() => onFocus(null)}>
      {[29, 60, 91].map((line) => <line key={line} x1="10" x2="330" y1={line} y2={line} className="elevation-gridline" />)}
      {series.map((item) => {
        if (item.points.length < 2) return null;
        const color = item.day.color || fallbackColor || "#9B6B2F";
        const linePoints = item.points.map((point) => `${x(point.distanceKm)},${y(point.elevation)}`).join(" ");
        return <g key={item.day.id}><polygon points={`${x(item.points[0].distanceKm)},96 ${linePoints} ${x(item.points.at(-1)!.distanceKm)},96`} fill={color} fillOpacity=".13" /><polyline points={linePoints} fill="none" stroke={color} strokeWidth="2.4" vectorEffect="non-scaling-stroke" /></g>;
      })}
      {fullTrip && series.slice(0, -1).map((item, index) => <g key={`boundary-${item.day.id}`} className="elevation-day-boundary"><line x1={x(item.offsetKm + item.distanceKm)} x2={x(item.offsetKm + item.distanceKm)} y1="17" y2="96" style={{ stroke: item.day.color || fallbackColor || "#9B6B2F" }} /><text x={x(item.offsetKm + item.distanceKm) + 3} y="15">第 {index + 2} 天</text></g>)}
      {landmarks.map((point) => <g key={`${point.dayId}-${point.placeId}`} className={`elevation-landmark${activePoint?.placeId === point.placeId ? " is-focused" : ""}`}><circle cx={x(point.distanceKm)} cy={y(point.elevation)} r="3.5" /><title>{point.name} · {Math.round(point.elevation)} m</title></g>)}
      {activePoint && <g className="elevation-cursor" pointerEvents="none"><line x1={x(activePoint.distanceKm)} x2={x(activePoint.distanceKm)} y1="20" y2="96" /><circle cx={x(activePoint.distanceKm)} cy={y(activePoint.elevation)} r="4" /><rect x={tooltipX} y="1" width="62" height="18" rx="3" /><text x={tooltipX + 31} y="13">{Math.round(activePoint.elevation)} m</text></g>}
      <rect className="elevation-hit-area" x="8" y="17" width="324" height="81" fill="transparent" />
    </svg>
    <footer><span><TrendingUp size={13} />累计上升 {Math.round(ascent)} m</span><span><TrendingDown size={13} />累计下降 {Math.round(descent)} m</span><span>{distance.toFixed(0)} km</span></footer>
  </section>;
}

function modeIcon(mode: TravelMode | undefined) {
  if (mode === "walking") return <Footprints size={15} />;
  if (mode === "riding") return <Bike size={15} />;
  return <CarFront size={15} />;
}

function modeCopy(mode: TravelMode | undefined) {
  if (mode === "walking") return "步行";
  if (mode === "riding") return "骑行";
  return "驾车";
}

function formatDistance(meters: number) {
  return meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(meters >= 10000 ? 0 : 1)} km`;
}

function formatMinutes(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours ? `${hours} 小时${rest ? ` ${rest} 分` : ""}` : `${rest} 分钟`;
}

function dayChecks(day: Day, nodes: Record<string, Place>, navigation: Navigation | undefined, canEdit: boolean) {
  const checks: string[] = [];
  if (navigation?.durationSeconds && navigation.durationSeconds > 8 * 3600) checks.push("当天预计在路上超过 8 小时，建议拆分或减少停靠点。");
  if (day.stops.length > 1 && !navigation?.segments?.length) checks.push(canEdit ? "分段距离尚未生成，请在地图中重算道路。" : "本日尚无分段道路数据。");
  const uncertain = day.stops.filter((stop) => nodes[stop.node]?.uncertain).length;
  if (uncertain) checks.push(`有 ${uncertain} 个地图落点尚未确认。`);
  const repeated = day.stops.filter((stop, index, stops) => stops.findIndex((candidate) => candidate.node === stop.node) !== index);
  if (repeated.length) checks.push(`当天重复经过 ${[...new Set(repeated.map((stop) => nodes[stop.node]?.name ?? stop.node))].join("、")}，请确认是否为计划中的往返。`);
  for (let index = 1; index < day.stops.length; index++) {
    const previous = nodes[day.stops[index - 1].node]?.elevation;
    const current = nodes[day.stops[index].node]?.elevation;
    if (previous !== undefined && current !== undefined && current - previous > 1000) {
      checks.push(`从${nodes[day.stops[index - 1].node].name}到${nodes[day.stops[index].node].name}海拔上升超过 1000 米，请预留适应时间。`);
      break;
    }
  }
  return checks;
}
